/**
 * Admin Sidebar - Navigation for admin panel
 * Based on CDFPI design
 */

import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Icons } from '../../components/Icons';
import { StorageService } from '../../services/storage';
import './AdminSidebar.css';

interface NavItem {
  to: string;
  icon: keyof typeof Icons;
  label: string;
  badge?: number;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

export function AdminSidebar() {
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [badges, setBadges] = useState({
    users: 0,
    support: 0,
    messages: 0,
  });

  // Fetch notification counts
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        // TODO: Implement badge fetching from API
      } catch (error) {
        console.error('Error fetching badges:', error);
      }
    };

    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, []);

  // Navigation sections
  const navSections: NavSection[] = [
    {
      title: 'Principal',
      items: [
        { to: '/admin', icon: 'BarChart', label: 'Tableau de bord' },
        { to: '/admin/users', icon: 'Users', label: 'Utilisateurs', badge: badges.users },
        { to: '/admin/wallets', icon: 'Wallet', label: 'Wallets' },
        { to: '/admin/portfolios', icon: 'PieChart', label: 'Portfolios' },
        { to: '/admin/support', icon: 'MessageSquare', label: 'Support', badge: badges.support },
        { to: '/admin/messages', icon: 'Mail', label: 'Messages', badge: badges.messages },
      ],
    },
    {
      title: 'Application',
      items: [
        { to: '/admin/analytics', icon: 'Activity', label: 'Analytics' },
        { to: '/admin/hosting', icon: 'Server', label: 'Hosting' },
        { to: '/admin/modules', icon: 'Package', label: 'Modules' },
        { to: '/admin/intelligence', icon: 'Sparkles', label: 'Intelligence IA' },
        { to: '/admin/api', icon: 'Code', label: 'API & Webhooks' },
      ],
    },
    {
      title: 'Contenu',
      items: [
        { to: '/admin/blog', icon: 'FileText', label: 'Blog' },
        { to: '/admin/pages', icon: 'Layout', label: 'Pages' },
        { to: '/admin/medias', icon: 'Image', label: 'Medias' },
      ],
    },
    {
      title: 'Showcase Site',
      items: [
        { to: '/admin/showcase', icon: 'Globe', label: 'Site vitrine' },
        { to: '/admin/showcase/seo', icon: 'Search', label: 'SEO' },
        { to: '/admin/showcase/analytics', icon: 'TrendingUp', label: 'Analytics' },
      ],
    },
    {
      title: 'Configuration',
      items: [
        { to: '/admin/settings', icon: 'Settings', label: 'Paramètres' },
        { to: '/admin/billing', icon: 'CreditCard', label: 'Facturation' },
        { to: '/admin/security', icon: 'Shield', label: 'Sécurité' },
        { to: '/admin/diagnostic', icon: 'Activity', label: 'Diagnostic' },
      ],
    },
  ];

  const renderNavItem = (item: NavItem) => {
    const IconComponent = Icons[item.icon] || Icons.Target;
    const isActive = location.pathname === item.to ||
      (item.to !== '/admin' && location.pathname.startsWith(item.to));

    return (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={() => setIsMobileOpen(false)}
        className={`admin-sidebar__nav-item ${isActive ? 'admin-sidebar__nav-item--active' : ''}`}
      >
        <IconComponent size={20} />
        <span className="admin-sidebar__nav-label">{item.label}</span>
        {item.badge && item.badge > 0 && (
          <span className="admin-sidebar__badge">
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile toggle button */}
      <button
        className="admin-sidebar__mobile-toggle"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label="Toggle menu"
      >
        {isMobileOpen ? <Icons.X size={20} /> : <Icons.Menu size={20} />}
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="admin-sidebar__overlay"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${isMobileOpen ? 'admin-sidebar--open' : ''}`}>
        {/* Header */}
        <div className="admin-sidebar__header">
          <NavLink to="/admin" className="admin-sidebar__brand">
            <div className="admin-sidebar__logo">
              <Icons.Shield size={24} />
            </div>
            <div className="admin-sidebar__brand-text">
              <span className="admin-sidebar__brand-name">Edouard.ai</span>
              <span className="admin-sidebar__brand-subtitle">Administration</span>
            </div>
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className="admin-sidebar__nav">
          {navSections.map((section) => (
            <div key={section.title} className="admin-sidebar__section">
              <span className="admin-sidebar__section-title">{section.title}</span>
              {section.items.map(renderNavItem)}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="admin-sidebar__footer">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="admin-sidebar__footer-link"
          >
            <Icons.ExternalLink size={16} />
            Voir l'application
          </a>
          <button
            onClick={() => {
              StorageService.remove('admin_key');
              window.location.href = '/admin';
            }}
            className="admin-sidebar__footer-link admin-sidebar__footer-link--danger"
          >
            <Icons.LogOut size={16} />
            Déconnexion
          </button>
        </div>
      </aside>
    </>
  );
}
